import { supabase, isDemoMode } from './supabase';
import { getFeatureFlags, getStudentTiers } from './featureService';
import {
  getDemoSightWords,     addDemoSightWord,     deleteDemoSightWord,
  getDemoScrambleWords,  addDemoScrambleWord,  deleteDemoScrambleWord,
  getDemoCategoryWords,  addDemoCategoryWord,  deleteDemoCategoryWord,
  getDemoAssignedSightIds,    assignDemoSightWord,    unassignDemoSightWord,
  getDemoAssignedScrambleIds, assignDemoScrambleWord, unassignDemoScrambleWord,
  getDemoAssignedCategoryIds, assignDemoCategoryWord, unassignDemoCategoryWord,
  getDemoMathConcepts,   addDemoMathConcept,   deleteDemoMathConcept,
  getDemoAssignedMathIds, assignDemoMathConcept, unassignDemoMathConcept,
} from './demoData';

// ─── SIGHT WORDS ────────────────────────────────────────────

export async function getSightWords() {
  if (isDemoMode) return { data: getDemoSightWords(), error: null };
  return supabase.from('sight_words').select('*').is('deleted_at', null).order('word');
}

export async function addSightWord(word, createdBy) {
  if (isDemoMode) {
    const entry = addDemoSightWord(word);
    return { data: entry, error: null };
  }
  return supabase
    .from('sight_words')
    .insert({ word: word.trim().toUpperCase(), created_by: createdBy })
    .select()
    .single();
}

export async function deleteSightWord(wordId) {
  if (isDemoMode) { deleteDemoSightWord(wordId); return { error: null }; }
  return supabase.from('sight_words').update({ deleted_at: new Date().toISOString() }).eq('id', wordId);
}

export async function getAssignedSightWords(studentId) {
  if (isDemoMode) {
    const ids = new Set(getDemoAssignedSightIds(studentId));
    return { data: getDemoSightWords().filter((w) => ids.has(w.id)), error: null };
  }
  const { data, error } = await supabase
    .from('student_sight_words').select('sight_words(*)').eq('student_id', studentId);
  if (error) return { data: null, error };
  return { data: data.map((row) => row.sight_words), error: null };
}

export async function assignSightWordToStudent({ studentId, wordId, assignedBy }) {
  if (isDemoMode) { assignDemoSightWord(studentId, wordId); return { error: null }; }
  return supabase.from('student_sight_words')
    .insert({ student_id: studentId, word_id: wordId, assigned_by: assignedBy });
}

export async function unassignSightWordFromStudent({ studentId, wordId }) {
  if (isDemoMode) { unassignDemoSightWord(studentId, wordId); return { error: null }; }
  return supabase.from('student_sight_words')
    .delete().eq('student_id', studentId).eq('word_id', wordId);
}

export async function getAssignedSightWordIds(studentId) {
  if (isDemoMode) return { data: getDemoAssignedSightIds(studentId), error: null };
  const { data, error } = await supabase
    .from('student_sight_words').select('word_id').eq('student_id', studentId);
  if (error) return { data: null, error };
  return { data: data.map((row) => row.word_id), error: null };
}

// ─── SCRAMBLE WORDS ─────────────────────────────────────────

export async function getScrambleWords() {
  if (isDemoMode) return { data: getDemoScrambleWords(), error: null };
  return supabase.from('scramble_words').select('*').is('deleted_at', null).order('word');
}

export async function addScrambleWord(word, scrambled, createdBy) {
  if (isDemoMode) {
    const entry = addDemoScrambleWord(word, scrambled);
    return { data: entry, error: null };
  }
  return supabase.from('scramble_words').insert({
    word: word.trim().toUpperCase(),
    scrambled: scrambled.trim().toUpperCase(),
    created_by: createdBy,
  }).select().single();
}

export async function deleteScrambleWord(wordId) {
  if (isDemoMode) { deleteDemoScrambleWord(wordId); return { error: null }; }
  return supabase.from('scramble_words').update({ deleted_at: new Date().toISOString() }).eq('id', wordId);
}

export async function getAssignedScrambleWords(studentId) {
  if (isDemoMode) {
    const ids = new Set(getDemoAssignedScrambleIds(studentId));
    return { data: getDemoScrambleWords().filter((w) => ids.has(w.id)), error: null };
  }
  const { data, error } = await supabase
    .from('student_scramble_words').select('scramble_words(*)').eq('student_id', studentId);
  if (error) return { data: null, error };
  return { data: data.map((row) => row.scramble_words), error: null };
}

export async function assignScrambleWordToStudent({ studentId, wordId, assignedBy }) {
  if (isDemoMode) { assignDemoScrambleWord(studentId, wordId); return { error: null }; }
  return supabase.from('student_scramble_words')
    .insert({ student_id: studentId, word_id: wordId, assigned_by: assignedBy });
}

export async function unassignScrambleWordFromStudent({ studentId, wordId }) {
  if (isDemoMode) { unassignDemoScrambleWord(studentId, wordId); return { error: null }; }
  return supabase.from('student_scramble_words')
    .delete().eq('student_id', studentId).eq('word_id', wordId);
}

export async function getAssignedScrambleWordIds(studentId) {
  if (isDemoMode) return { data: getDemoAssignedScrambleIds(studentId), error: null };
  const { data, error } = await supabase
    .from('student_scramble_words').select('word_id').eq('student_id', studentId);
  if (error) return { data: null, error };
  return { data: data.map((row) => row.word_id), error: null };
}

// ─── CATEGORY WORDS ─────────────────────────────────────────

export async function getCategoryWords() {
  if (isDemoMode) return { data: getDemoCategoryWords(), error: null };
  return supabase.from('category_words').select('*').is('deleted_at', null).order('word');
}

export async function addCategoryWord(word, correctCategory, distractors, createdBy) {
  if (isDemoMode) {
    const entry = addDemoCategoryWord(word, correctCategory, distractors);
    return { data: entry, error: null };
  }
  return supabase.from('category_words').insert({
    word: word.trim().toUpperCase(),
    correct_category: correctCategory.trim().toUpperCase(),
    distractors: distractors.map((d) => d.trim().toUpperCase()),
    created_by: createdBy,
  }).select().single();
}

export async function deleteCategoryWord(wordId) {
  if (isDemoMode) { deleteDemoCategoryWord(wordId); return { error: null }; }
  return supabase.from('category_words').update({ deleted_at: new Date().toISOString() }).eq('id', wordId);
}

export async function getAssignedCategoryWords(studentId) {
  if (isDemoMode) {
    const ids = new Set(getDemoAssignedCategoryIds(studentId));
    return { data: getDemoCategoryWords().filter((w) => ids.has(w.id)), error: null };
  }
  const { data, error } = await supabase
    .from('student_category_words').select('category_words(*)').eq('student_id', studentId);
  if (error) return { data: null, error };
  return { data: data.map((row) => row.category_words), error: null };
}

export async function assignCategoryWordToStudent({ studentId, wordId, assignedBy }) {
  if (isDemoMode) { assignDemoCategoryWord(studentId, wordId); return { error: null }; }
  return supabase.from('student_category_words')
    .insert({ student_id: studentId, word_id: wordId, assigned_by: assignedBy });
}

export async function unassignCategoryWordFromStudent({ studentId, wordId }) {
  if (isDemoMode) { unassignDemoCategoryWord(studentId, wordId); return { error: null }; }
  return supabase.from('student_category_words')
    .delete().eq('student_id', studentId).eq('word_id', wordId);
}

export async function getAssignedCategoryWordIds(studentId) {
  if (isDemoMode) return { data: getDemoAssignedCategoryIds(studentId), error: null };
  const { data, error } = await supabase
    .from('student_category_words').select('word_id').eq('student_id', studentId);
  if (error) return { data: null, error };
  return { data: data.map((row) => row.word_id), error: null };
}

// ─── MATH CONCEPTS ───────────────────────────────────────────

export async function getMathConcepts() {
  if (isDemoMode) return { data: getDemoMathConcepts(), error: null };
  return supabase.from('math_concepts').select('*').is('deleted_at', null).order('display_order');
}

export async function addMathConcept({ stage, type, question, visual, dots, expression, answer, choices, createdBy }) {
  if (isDemoMode) {
    const entry = addDemoMathConcept(stage, type, question, visual, dots, expression, answer, choices);
    return { data: entry, error: null };
  }
  return supabase.from('math_concepts').insert({
    stage, type, question,
    visual: visual || null,
    dots: dots != null ? parseInt(dots, 10) : null,
    expression: expression || null,
    answer: parseInt(answer, 10),
    choices: choices.map(Number),
    created_by: createdBy,
  }).select().single();
}

export async function deleteMathConcept(conceptId) {
  if (isDemoMode) { deleteDemoMathConcept(conceptId); return { error: null }; }
  return supabase.from('math_concepts').update({ deleted_at: new Date().toISOString() }).eq('id', conceptId);
}

export async function getAssignedMathConcepts(studentId) {
  if (isDemoMode) {
    const ids = new Set(getDemoAssignedMathIds(studentId));
    return { data: getDemoMathConcepts().filter((c) => ids.has(c.id)), error: null };
  }
  const { data, error } = await supabase
    .from('student_math_concepts').select('math_concepts(*)').eq('student_id', studentId);
  if (error) return { data: null, error };
  return { data: data.map((row) => row.math_concepts), error: null };
}

export async function assignMathConceptToStudent({ studentId, conceptId, assignedBy }) {
  if (isDemoMode) { assignDemoMathConcept(studentId, conceptId); return { error: null }; }
  return supabase.from('student_math_concepts')
    .insert({ student_id: studentId, concept_id: conceptId, assigned_by: assignedBy });
}

export async function unassignMathConceptFromStudent({ studentId, conceptId }) {
  if (isDemoMode) { unassignDemoMathConcept(studentId, conceptId); return { error: null }; }
  return supabase.from('student_math_concepts')
    .delete().eq('student_id', studentId).eq('concept_id', conceptId);
}

export async function getAssignedMathConceptIds(studentId) {
  if (isDemoMode) return { data: getDemoAssignedMathIds(studentId), error: null };
  const { data, error } = await supabase
    .from('student_math_concepts').select('concept_id').eq('student_id', studentId);
  if (error) return { data: null, error };
  return { data: data.map((row) => row.concept_id), error: null };
}

// Returns concepts assigned to this student; if none assigned, returns all concepts (default set)
export async function getMathConceptsForStudent(studentId) {
  if (isDemoMode) {
    const ids = getDemoAssignedMathIds(studentId);
    if (ids.length === 0) return { data: getDemoMathConcepts(), error: null };
    const set = new Set(ids);
    return { data: getDemoMathConcepts().filter((c) => set.has(c.id)), error: null };
  }
  const { data: assigned, error } = await supabase
    .from('student_math_concepts').select('math_concepts(*)').eq('student_id', studentId);
  if (error) return { data: null, error };
  if (assigned && assigned.length > 0) {
    return { data: assigned.map((r) => r.math_concepts).sort((a, b) => a.display_order - b.display_order), error: null };
  }
  // No assignments — return all (default)
  return supabase.from('math_concepts').select('*').is('deleted_at', null).order('display_order');
}

// ─── TIER-AWARE WORD FETCHING ─────────────────────────────────
// Used by all three activity components.
// Tier 1 (rows 1-25 by created_at): always available to all students.
// Tier 2 (rows 26-500): requires global flag + student grant.
// Tier 3 (rows 501+):  requires global flag + student grant.
// Words are shuffled randomly so each session feels fresh.

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const TABLE = { sight: 'sight_words', scramble: 'scramble_words', category: 'category_words' };
const TIER_FLAG = {
  sight:    { 2: 'sight_words_tier2_enabled',    3: 'sight_words_tier3_enabled' },
  scramble: { 2: 'scramble_words_tier2_enabled', 3: 'scramble_words_tier3_enabled' },
  category: { 2: 'category_words_tier2_enabled', 3: 'category_words_tier3_enabled' },
};

export async function getWordsForActivity(studentId, activityType) {
  if (isDemoMode) {
    // Demo: return all demo words shuffled (treated as tier 1)
    const demoFns = {
      sight: getDemoSightWords,
      scramble: getDemoScrambleWords,
      category: getDemoCategoryWords,
    };
    return { data: shuffle(demoFns[activityType]()), error: null };
  }

  const table = TABLE[activityType];
  if (!table) return { data: [], error: new Error('Unknown activity type') };

  // Load flags and student tier grants in parallel
  const [flagsResult, tiersResult] = await Promise.all([
    getFeatureFlags(),
    getStudentTiers(studentId, activityType),
  ]);

  const flags = flagsResult.data || {};
  const studentTiers = tiersResult.data || new Set();

  // Always fetch tier 1 (rows 0-24)
  const fetches = [
    supabase.from(table).select('*').is('deleted_at', null).order('created_at', { ascending: true }).range(0, 24),
  ];

  if (flags[TIER_FLAG[activityType][2]] && studentTiers.has(2)) {
    fetches.push(
      supabase.from(table).select('*').is('deleted_at', null).order('created_at', { ascending: true }).range(25, 499)
    );
  }
  if (flags[TIER_FLAG[activityType][3]] && studentTiers.has(3)) {
    fetches.push(
      supabase.from(table).select('*').is('deleted_at', null).order('created_at', { ascending: true }).range(500, 9999)
    );
  }

  const results = await Promise.all(fetches);
  const firstError = results.find(r => r.error)?.error || null;
  const combined = results.flatMap(r => r.data || []);

  return { data: shuffle(combined), error: firstError };
}

// ─── CONTENT COUNTS ──────────────────────────────────────────

export async function getContentCounts() {
  if (isDemoMode) {
    return {
      sightWords:    getDemoSightWords().length,
      scrambleWords: getDemoScrambleWords().length,
      categoryWords: getDemoCategoryWords().length,
      mathConcepts:  getDemoMathConcepts().length,
      error: null,
    };
  }
  const [sight, scramble, category, math] = await Promise.all([
    supabase.from('sight_words').select('id', { count: 'exact', head: true }).is('deleted_at', null),
    supabase.from('scramble_words').select('id', { count: 'exact', head: true }).is('deleted_at', null),
    supabase.from('category_words').select('id', { count: 'exact', head: true }).is('deleted_at', null),
    supabase.from('math_concepts').select('id', { count: 'exact', head: true }).is('deleted_at', null),
  ]);
  return {
    sightWords:    sight.count ?? 0,
    scrambleWords: scramble.count ?? 0,
    categoryWords: category.count ?? 0,
    mathConcepts:  math.count ?? 0,
    error: sight.error || scramble.error || category.error || math.error || null,
  };
}

// ─── SOFT DELETE & RESTORE ──────────────────────────────────

export async function getDeletedWords(type) {
  if (isDemoMode) return { data: [], error: null };
  const table = TABLE[type];
  if (!table) return { data: [], error: new Error('Unknown word type') };
  const { data, error } = await supabase.from(table).select('*').not('deleted_at', 'is', null).order('deleted_at', { ascending: false });
  return { data: data || [], error };
}

export async function restoreWord(type, wordId) {
  if (isDemoMode) return { error: null };
  const table = TABLE[type];
  if (!table) return { error: new Error('Unknown word type') };
  return supabase.from(table).update({ deleted_at: null }).eq('id', wordId);
}

export async function permanentlyDeleteWord(type, wordId) {
  if (isDemoMode) return { error: null };
  const table = TABLE[type];
  if (!table) return { error: new Error('Unknown word type') };
  return supabase.from(table).delete().eq('id', wordId);
}

export async function softDeleteWords(type, wordIds) {
  if (isDemoMode) return { error: null };
  if (wordIds.length === 0) return { error: null };
  const table = TABLE[type];
  if (!table) return { error: new Error('Unknown word type') };
  return supabase.from(table).update({ deleted_at: new Date().toISOString() }).in('id', wordIds);
}
